import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { FiSend, FiTrash2, FiEdit2 } from 'react-icons/fi';

import Drawer from '../../components/ui/Drawer';
import Button from '../../components/ui/Button';
import StatusPill from '../../components/ui/StatusPill';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ErrorBanner from '../../components/ui/ErrorBanner';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import DocumentList from '../../components/ui/DocumentList';
import WorkflowTimeline from '../../components/ui/WorkflowTimeline';
import WorkflowActions from '../../components/ui/WorkflowActions';
import CurrencyDisplay from '../../components/shared/CurrencyDisplay';
import DateDisplay from '../../components/shared/DateDisplay';

import { getExpense, submitExpense, deleteExpense, updateExpense } from '../../api/expenses';
import { getWorkflowForEntity } from '../../api/workflows';
import { extractErrorMessage } from '../../api/client';
import { WorkflowStatus } from '../../enums';
import { useAuth } from '../../auth/AuthContext';
import ExpenseFormInner from './ExpenseFormInner';

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      <span className="text-sm text-ink">{children ?? <span className="text-ink-faint">—</span>}</span>
    </div>
  );
}

export default function ExpenseDetailDrawer({ open, expenseId, onClose }) {
  const qc = useQueryClient();
  const { user, isAdmin, username } = useAuth();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editErrors, setEditErrors] = useState({});

  const {
    data: expense,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['expenses', expenseId],
    queryFn: () => getExpense(expenseId),
    enabled: !!open && !!expenseId,
  });

  const { data: workflow } = useQuery({
    queryKey: ['workflows', 'EXPENSE', expenseId],
    queryFn: () => getWorkflowForEntity('EXPENSE', expenseId),
    enabled: !!open && !!expenseId && !!expense?.workflowId,
  });

  React.useEffect(() => {
    if (!open) {
      setIsEditing(false);
    }
  }, [open]);

  React.useEffect(() => {
    if (expense && isEditing) {
      setEditForm({
        title: expense.title || '',
        description: expense.description || '',
        amount: expense.amount || '',
        category: expense.category || 'OTHER',
        expenseType: expense.expenseType || '',
        paymentType: expense.paymentType || '',
        department: expense.department || '',
        vendorId: expense.vendorId || null,
        productId: expense.productId || '',
        isRecurring: !!expense.recurring,
        recurringType: expense.recurringType || '',
        transactionDate: expense.transactionDate || '',
        bankDescription: expense.bankDescription || '',
        comments: expense.comments || '',
      });
      setEditErrors({});
    }
  }, [expense, isEditing]);

  const updateMut = useMutation({
    mutationFn: (payload) => updateExpense(expenseId, payload),
    onSuccess: () => {
      toast.success('Expense updated');
      qc.invalidateQueries({ queryKey: ['expenses'] });
      setIsEditing(false);
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  function validateEdit() {
    const next = {};
    if (!editForm.title?.trim()) next.title = 'Title is required';
    if (!editForm.amount || Number(editForm.amount) <= 0)
      next.amount = 'Amount must be greater than zero';
    if (!editForm.category) next.category = 'Category is required';
    if (!editForm.expenseType) next.expenseType = 'Expense Type is required';
    if (!editForm.paymentType) next.paymentType = 'Payment Type is required';
    if (!editForm.department) next.department = 'Department is required';
    if (editForm.isRecurring && !editForm.recurringType)
      next.recurringType = 'Pick a recurring cadence';
    setEditErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSaveEdit() {
    if (!validateEdit()) return;
    updateMut.mutate({
      title: editForm.title.trim(),
      description: editForm.description.trim() || null,
      amount: Number(editForm.amount),
      category: editForm.category,
      expenseType: editForm.expenseType,
      paymentType: editForm.paymentType,
      department: editForm.department,
      vendorId: editForm.vendorId || null,
      productId: editForm.productId ? Number(editForm.productId) : null,
      isRecurring: !!editForm.isRecurring,
      recurringType: editForm.isRecurring ? editForm.recurringType || null : null,
      transactionDate: editForm.transactionDate || null,
      bankDescription: editForm.bankDescription.trim() || null,
      comments: editForm.comments.trim() || null,
    });
  }

  const submitMut = useMutation({
    mutationFn: () => submitExpense(expenseId),
    onSuccess: () => {
      toast.success('Expense submitted for approval');
      qc.invalidateQueries({ queryKey: ['expenses'] });
      qc.invalidateQueries({ queryKey: ['workflows'] });
      setConfirmSubmit(false);
    },
    onError: (e) => {
      toast.error(extractErrorMessage(e));
      setConfirmSubmit(false);
    },
  });

  const deleteMut = useMutation({
    mutationFn: () => deleteExpense(expenseId),
    onSuccess: () => {
      toast.success('Expense deleted');
      qc.invalidateQueries({ queryKey: ['expenses'] });
      setConfirmDelete(false);
      onClose?.();
    },
    onError: (e) => {
      toast.error(extractErrorMessage(e));
      setConfirmDelete(false);
    },
  });

  const isOwner =
    !!expense && !!user && (
      (expense.createdBy && user.id && Number(expense.createdBy) === Number(user.id)) ||
      (expense.createdByUsername && username && expense.createdByUsername === username)
    );
  const status = expense?.workflowStatus;
  const isDraft = status === WorkflowStatus.DRAFT;
  const canSubmit = isDraft && (isOwner || isAdmin);
  const canDelete = isOwner || isAdmin;
  const canEditDocs = (isOwner && isDraft) || isAdmin;
  const canEdit = isDraft && (isOwner || isAdmin);

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        title={expense ? expense.title : 'Expense'}
        width="lg"
        footer={
          expense ? (
            isEditing ? (
              <div className="flex w-full items-center justify-end gap-2">
                <Button variant="ghost" onClick={() => setIsEditing(false)} disabled={updateMut.isPending}>
                  Cancel
                </Button>
                <Button variant="primary" loading={updateMut.isPending} onClick={handleSaveEdit}>
                  Save Changes
                </Button>
              </div>
            ) : (
              <div className="flex w-full flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  {canSubmit && (
                    <Button
                      variant="primary"
                      icon={FiSend}
                      onClick={() => setConfirmSubmit(true)}
                      loading={submitMut.isPending}
                    >
                      Submit for approval
                    </Button>
                  )}
                  {canEdit && (
                    <Button
                      variant="ghost"
                      icon={FiEdit2}
                      onClick={() => setIsEditing(true)}
                    >
                      Edit
                    </Button>
                  )}
                  {canDelete && (
                    <Button
                      variant="danger"
                      icon={FiTrash2}
                      onClick={() => setConfirmDelete(true)}
                      disabled={!isDraft && !isAdmin}
                    >
                      Delete
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {workflow && (
                    <WorkflowActions
                      workflow={workflow}
                      invalidateKeys={[['expenses'], ['expenses', expenseId]]}
                    />
                  )}
                </div>
              </div>
            )
          ) : null
        }
      >
        {isLoading ? (
          <LoadingSpinner label="Loading expense..." />
        ) : error ? (
          <ErrorBanner error={error} />
        ) : !expense ? null : isEditing ? (
          <div className="space-y-8">
            <ExpenseFormInner
              form={editForm}
              update={(patch) => setEditForm((f) => ({ ...f, ...patch }))}
              errors={editErrors}
              isEdit={true}
            />
            <div className="border-t border-slate-200 pt-6">
              <h4 className="text-base font-semibold text-slate-800 mb-4">Attachments</h4>
              <DocumentList
                entityType="EXPENSE"
                entityId={expense.id}
                department={expense.department}
                canUpload={canEditDocs}
                canDelete={canEditDocs}
              />
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <header className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 pb-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-ink-muted">
                  Expense #{expense.id}
                </div>
                <h3 className="mt-0.5 text-lg font-semibold text-ink">
                  {expense.title}
                </h3>
                {expense.description && (
                  <p className="mt-1 max-w-prose text-sm text-ink-muted">
                    {expense.description}
                  </p>
                )}
              </div>
              <div className="text-right">
                <div className="text-2xl font-semibold text-ink">
                  <CurrencyDisplay value={expense.amount} />
                </div>
                <div className="mt-1">
                  <StatusPill value={status} />
                </div>
              </div>
            </header>

            <section className="grid grid-cols-2 gap-4 md:grid-cols-3">
              <Field label="Expense Type">{expense.expenseType}</Field>
              <Field label="Payment Type">{expense.paymentType}</Field>
              <Field label="Category">
                {expense.category?.replace(/_/g, ' ')}
              </Field>
              <Field label="Department">{expense.department}</Field>
              <Field label="Vendor">
                {expense.vendorName ? expense.vendorName : (expense.vendorId ? `#${expense.vendorId}` : null)}
              </Field>
              <Field label="Product">
                {expense.productName ? expense.productName : (expense.productId ? `#${expense.productId}` : null)}
              </Field>
              <Field label="Recurring">
                {expense.recurring
                  ? expense.recurringType?.replace(/_/g, ' ') || 'Yes'
                  : 'No'}
              </Field>
              <Field label="Created">
                <DateDisplay value={expense.createdAt} withTime />
              </Field>
              <Field label="Created by">{expense.createdByUsername || `#${expense.createdBy}`}</Field>
              <Field label="Approved by">
                {expense.approvedByUsername ? expense.approvedByUsername : (expense.approvedBy ? `#${expense.approvedBy}` : null)}
              </Field>
              <Field label="Last updated">
                <DateDisplay value={expense.updatedAt} withTime />
              </Field>
            </section>

            {workflow && (
              <section className="rounded-lg border border-slate-200 bg-slate-50/50 px-4 py-4">
                <WorkflowTimeline workflow={workflow} />
              </section>
            )}

            <section>
              <DocumentList
                entityType="EXPENSE"
                entityId={expense.id}
                department={expense.department}
                canUpload={canEditDocs}
                canDelete={canEditDocs}
              />
            </section>
          </div>
        )}
      </Drawer>

      <ConfirmDialog
        open={confirmSubmit}
        title="Submit for approval?"
        message="Once submitted, you won't be able to edit attachments or delete the draft."
        confirmLabel="Submit"
        variant="primary"
        loading={submitMut.isPending}
        onConfirm={() => submitMut.mutate()}
        onClose={() => setConfirmSubmit(false)}
      />

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this expense?"
        message="This action cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        loading={deleteMut.isPending}
        onConfirm={() => deleteMut.mutate()}
        onClose={() => setConfirmDelete(false)}
      />
    </>
  );
}
